import store from '@/store';
import {
  Module,
  VuexModule,
  Mutation,
  getModule,
  Action
} from 'vuex-module-decorators';
import { cloneDeep } from 'lodash-es';
import {
  IPensionLead,
  IPensionSimulationData,
  PensionContractType
} from '@/modules/pension/types';
import { getCustomer } from '@/modules/common/services/http/account';
import { PensionAPI } from '@/modules/pension/services/pension';
import { PensionState as InitalState } from './state';
import {
  State,
  Actions,
  PayloadModalOpen,
  PayloadLoading,
  PayloadModalLeadSuccess,
  PayloadErrors,
  PayloadSimulatorResult,
  KeysModal,
  PayloadState,
  PayloadLead,
  PayloadAuthUser,
  PayloadSimulatorConfig,
  PayloadSimulatorForm
} from './types';

@Module({
  dynamic: true,
  namespaced: true,
  name: 'PensionLandingModule',
  store
})
export class VuexPensionLandingModule extends VuexModule implements Actions {
  // Module state
  public data: State = cloneDeep(InitalState);

  /**
   * Set entire state object.
   */
  @Action({ rawError: true })
  public setState(data: any) {
    this.context.commit('SET_STATE', { data });
  }

  /**
   * Generic open modal to available pension modals.
   */
  @Action({ rawError: true })
  public openModal(keyModal: KeysModal) {
    this.context.commit('SET_MODAL_OPEN', { state: true, keyModal });
    if (keyModal === 'lead') {
      this.context.commit('SET_MODAL_LEAD_SUCCESS', { state: false });
    }
  }

  /**
   * Generic close modal to available pension modals.
   */
  @Action({ rawError: true })
  public closeModal(keyModal: KeysModal) {
    this.context.commit('SET_MODAL_OPEN', { state: false, keyModal });
    if (keyModal === 'lead') {
      this.clearLeadInfo();
      this.context.commit('SET_MODAL_LEAD_SUCCESS', { state: false });
    }
  }

  /**
   * Generic loading state setter.
   */
  @Action({ rawError: true })
  public setLoading({ state, key }: PayloadLoading) {
    this.context.commit('SET_LOADING', { state, key });
  }

  /**
   * Generic set lead.
   */
  @Action({ rawError: true })
  public setLead(lead: IPensionLead) {
    this.context.commit('SET_LEAD', { lead });
  }

  /**
   * Clear lead data to its defaults.
   */
  @Action({ rawError: true })
  public clearLeadInfo() {
    const { lead } = this.data;
    const payload = {
      name: '',
      phone: '',
      email: '',
      type: lead.type,
      monthlyIncome: undefined,
      simulation: lead.simulation
    };
    this.setLead(payload);
  }

  /**
   * Attempt to fetch current auth user.
   */
  @Action({ rawError: true })
  public async fetchAuthUser() {
    if (localStorage.getItem('warren.accounts.accessToken')) {
      const { customer } = await getCustomer();
      const user = {
        apiId: customer._id,
        name: customer.personalProfile.fullName,
        email: customer.email,
        phone:
          customer.personalProfile.mobile || customer.personalProfile.phone,
        monthlyIncome: customer.suitability.question_income
      };
      this.context.commit('SET_AUTH_USER', { user });
    }
  }

  /**
   * Gets simulator default config.
   */
  @Action({ rawError: true })
  public async fetchSimulatorConfig() {
    try {
      this.setLoading({ state: true, key: 'simulatorConfig' });
      const api = new PensionAPI();
      const config = await api.getSimulatorConfig();
      this.context.commit('SET_SIMULATOR_CONFIG', { config });
    } catch (errors) {
      this.context.commit('SET_ERRORS', {
        errors,
        keyError: 'simulatorConfig'
      });
    } finally {
      this.setLoading({ state: false, key: 'simulatorConfig' });
    }
  }

  /**
   * Build partial lead data from auth user, if found.
   */
  @Action({ rawError: true })
  public buildLeadFromAuthUser() {
    const { authUser, lead } = this.data;
    if (authUser) {
      const payload: IPensionLead = {
        ...lead,
        apiId: authUser.apiId,
        name: authUser.name,
        email: authUser.email,
        phone: authUser.phone,
        monthlyIncome: authUser.monthlyIncome
      };
      this.setLead(payload);
    }
  }

  /**
   * Build simulator chart based on current simulator result.
   */
  @Action({ rawError: true })
  public buildSimulatorChart() {
    const { simulator } = this.data;
    const { chart } = simulator.result!;
    if (chart && chart.length >= 2) {
      const dataCDI = [...chart[1].data];
      const dataPension = [...chart[0].data];

      simulator.chart.datasets![0].data = dataCDI;
      simulator.chart.datasets![1].data = dataPension;

      const labelsArray = Array.from(
        { length: chart[0].data.length },
        () => ''
      );
      if (simulator.chart.labels) {
        simulator.chart.labels = [...labelsArray];
      }
    }
  }

  /**
   * Gets simulator default config.
   */
  @Action({ rawError: true })
  public async simulatePension() {
    try {
      this.setLoading({ state: true, key: 'simulatorSubmit' });
      const { simulator, lead } = this.data;
      const api = new PensionAPI();
      const response = await api.simulate(
        <IPensionSimulationData>simulator.form
      );

      const result = {
        chart: response.chart,
        maxAge: response.maxAge,
        totalValue: response.totalValue
      };

      const simulation = {
        ...simulator.form,
        totalValue: result.totalValue
      };

      const leadPayload = {
        ...lead,
        simulation
      };

      this.context.commit('SET_SIMULATOR_RESULT', { result });
      this.setLead(leadPayload);
      this.buildSimulatorChart();
    } catch (errors) {
      this.context.commit('SET_ERRORS', {
        errors,
        keyError: 'simulatorSimulate'
      });
    } finally {
      this.setLoading({ state: false, key: 'simulatorSubmit' });
    }
  }

  /**
   * Reset simulator and its data.
   */
  @Action({ rawError: true })
  public async resetSimulator() {
    const form = InitalState.simulator.form;
    this.clearLeadInfo();
    this.context.commit('SET_SIMULATOR_FORM', { form });
    this.context.commit('SET_SIMULATOR_RESULT', { result: undefined });
  }

  /**
   * Validate if current lead is ok to be sent to backend.
   */
  @Action({ rawError: true })
  public async validateLead() {
    const { lead, validator } = this.data;
    if (!lead.simulation) {
      throw new Error('Simulação não está realizada.');
    }
    const data = {
      name: lead.name,
      email: lead.email,
      phone: `+55${lead.phone}`
    };
    await validator.lead.validate(data);
    return true;
  }

  /**
   * Handle logic on asking for a new contact.
   */
  @Action({ rawError: true })
  public simulatorAskForContact(type?: PensionContractType) {
    const { lead, authUser, modal, simulator } = this.data;

    // Change type of contact if given
    if (type) {
      this.setLead({ ...lead, type });
    }

    // Copy values from auth user to lead
    if (authUser) {
      this.buildLeadFromAuthUser();
    }

    // Handle opening modal/send lead
    if (!modal.lead.open && !authUser) {
      if (simulator.result) {
        this.openModal('lead');
      } else {
        throw new Error('Simulação não está realizada.');
      }
    } else {
      this.sendLead();
    }
  }

  /**
   * Opens lead modal but with success message.
   */
  @Action({ rawError: true })
  public openModalLeadSuccess() {
    this.openModal('lead');
    this.context.commit('SET_MODAL_LEAD_SUCCESS', { state: true });
  }

  /**
   * Attempt to send lead to backend/API.
   */
  @Action({ rawError: true })
  public async sendLead(lead?: IPensionLead) {
    const { loading, authUser, lead: leadState } = this.data;

    if (loading.sendLead) {
      return;
    }

    // Override current lead, if given
    if (lead) {
      this.setLead(lead);
    }

    try {
      this.setLoading({ state: true, key: 'sendLead' });

      // Check lead data only if user is visitor, otherwise
      // lead data comes from current auth user which has appId,
      // but phone like *******99
      if (!authUser && !lead) {
        await this.validateLead();
      }

      // Send lead
      const api = new PensionAPI();
      await api.pensionNotify(leadState);
      this.openModalLeadSuccess();
    } finally {
      this.setLoading({ state: false, key: 'sendLead' });
    }
  }

  @Mutation
  public SET_STATE({ data }: PayloadState) {
    this.data = cloneDeep(data);
  }

  @Mutation
  public SET_LEAD({ lead }: PayloadLead) {
    this.data.lead = { ...lead };
  }

  @Mutation
  public SET_AUTH_USER({ user }: PayloadAuthUser) {
    this.data.authUser = { ...user };
  }

  @Mutation
  public SET_SIMULATOR_CONFIG({ config }: PayloadSimulatorConfig) {
    this.data.simulator.config = { ...config };
  }

  @Mutation
  public SET_SIMULATOR_FORM({ form }: PayloadSimulatorForm) {
    this.data.simulator.form = { ...form };
  }

  @Mutation
  public SET_SIMULATOR_RESULT({ result }: PayloadSimulatorResult) {
    this.data.simulator.result = result;
  }

  @Mutation
  public SET_MODAL_OPEN({ state, keyModal }: PayloadModalOpen) {
    this.data.modal[keyModal].open = state;
  }

  @Mutation
  public SET_MODAL_LEAD_SUCCESS({ state }: PayloadModalLeadSuccess) {
    this.data.modal.lead.success = state;
  }

  @Mutation
  public SET_LOADING({ state, key }: PayloadLoading) {
    this.data.loading[key] = state;
  }

  @Mutation
  public SET_ERRORS({ errors, keyError }: PayloadErrors) {
    this.data.errors[keyError] = errors;
  }
}

export const PensionLandingState = InitalState;

export const PensionLandingModule = getModule(VuexPensionLandingModule);
